import React, { FC, useMemo, useState } from 'react'
import ReportSummaryModule from './ReportSummaryModule'
import Loading from '../common/Loading'
import { secToHour } from '../../../utils/date'
import InfoTips from '../common/InfoTips'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from '../common/Tips'
import DetailRow from './DetailRow'

interface Props {
  member: MemberDetail | undefined
}

interface MemberDetail {
  id: number
  name: string
  profile_image_url?: string | null
  label_groups: LabelGroup[]
  labels: Label[]
}

interface ListDetail {
  id: number
  name: string
  code?: string | null
  color: string
  time: number
  cost: number
}

interface LabelGroup extends ListDetail {
  labels: ListDetail[]
}

interface Label extends ListDetail {
  label_groups: ListDetail[]
}

const MemberSummaryModule: FC<Props> = (props) => {
  const {
    member,
  } = props

  const {
    login,
  } = useSelector((state: RootState) => state)

  const [isDisplayProjectWorkTime, setIsDisplayProjectWorkTime] = useState<boolean>(true)
  const [isDisplayLabelWorkTime, setIsDisplayLabelWorkTime] = useState<boolean>(true)

  const [isDisplayLabelGroupDetail, setIsDisplayLabelGroupDetail] = useState<boolean>(false)
  const [isDisplayLabelDetail, setIsDisplayLabelDetail] = useState<boolean>(false)

  const labelGroupList = useMemo(() => {
    if (!member) return []
    let tmpList: LabelGroup[] = []
    member.label_groups.forEach((v) => {
      if (!tmpList.map((w) => w.name).includes(v.name)) {
        tmpList.push(v as LabelGroup)
      } else {
        tmpList = tmpList.map((w) => {
          if (w.name === v.name) return { ...w, time: w.time + v.time }
          return w
        })
      }
    })
    return tmpList
  }, [member])

  const labelList = useMemo(() => {
    if (!member) return []
    let tmpList: Label[] = []
    member.labels.forEach((v) => {
      if (!tmpList.map((w) => w.name).includes(v.name)) {
        tmpList.push(v as Label)
      } else {
        tmpList = tmpList.map((w) => {
          if (w.name === v.name) return { ...w, time: w.time + v.time }
          return w
        })
      }
    })
    return tmpList
  }, [member])

  const maxTime = useMemo(() => {
    if (!member) return 0
    const tmpMax = member.labels.reduce((s, w) => s + w.time, 0)
    return (secToHour(tmpMax) + 1) * 3600
  }, [member])

  return (
    <>
      <div className="summary_title">
        工数管理情報
      </div>
      <InfoTips>
        <>
          <div>
            工数管理機能で仕分けされた時間を集計しています
          </div>
          <div>
            それぞれプロジェクト別・業務別の割合と時間を表示しています
          </div>
        </>
      </InfoTips>
      {
        member ? (
          <>
            {
              maxTime > 3600 ? (
                <>
                  <div className="category_title_area">
                    <div
                      className="category_title"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      プロジェクト別仕分け実績情報
                      <div className="tools" style={{ justifyContent: "space-between" }}>
                        {
                          login.permissions?.task_cost_view && (
                            <>
                              {
                                isDisplayProjectWorkTime ? (
                                  <div />
                                ) : (
                                  <div className="cost_viewable_label">
                                    コスト表示権限を持つユーザーのみ表示されています
                                  </div>
                                )
                              }
                              <button
                                type="button"
                                className={`display_switch ${isDisplayProjectWorkTime ? "check" : ""}`}
                                onClick={() => setIsDisplayProjectWorkTime((cur) => !cur)}
                              >
                                <div>
                                  <FontAwesomeIcon icon={['far', 'clock']} />
                                  <span>
                                    実績時間
                                  </span>
                                </div>
                                <div>
                                  <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                  <span>
                                    実績コスト
                                  </span>
                                </div>
                              </button>
                            </>
                          )
                        }
                      </div>
                    </div>
                  </div>
                  <ReportSummaryModule
                    list={labelGroupList}
                    type="time"
                    graphTitle="プロジェクト別仕分け実績情報"
                    headerTitle={`プロジェクト(実績${isDisplayProjectWorkTime ? "時間" : "コスト"}が多い順)`}
                    chartSize={300}
                    valueType={isDisplayProjectWorkTime ? "time" : "cost"}
                  />
                  {
                    isDisplayLabelGroupDetail ? (
                      <>
                        {
                          labelGroupList.length ? (
                            <>
                              <div className="list_row_wrapper">
                                <div className={`list_row header_area${login.permissions?.task_cost_view ? " cost_view" : ""}`}>
                                  <div className="title_display_area">
                                    プロジェクト名
                                  </div>
                                  <div className="work_time">
                                    業務時間
                                  </div>
                                  {
                                    login.permissions?.task_cost_view && (
                                      <div className="work_cost">
                                        業務コスト
                                        <Tips
                                          description="コスト表示権限を持つユーザーのみ表示されています"
                                          className="fit"
                                          icon={false}
                                        >
                                          <>
                                            <FontAwesomeIcon icon={['far', 'question-circle']} />
                                          </>
                                        </Tips>
                                      </div>
                                    )
                                  }
                                </div>
                              </div>
                              <div>
                                {
                                  labelGroupList.map((labelGroup) => (
                                    <DetailRow
                                      key={`detail_row_${labelGroup.name}_${labelGroup.id}`}
                                      list={{
                                        ...labelGroup,
                                        details: labelGroup.labels,
                                      }}
                                      type="label_group"
                                      costView={login.permissions?.task_cost_view || false}
                                      isProject
                                      round
                                    />
                                  ))
                                }
                              </div>
                            </>
                          ) : (
                            <div
                              style={{
                                marginBottom: "24px",
                                fontSize: "14px",
                                textAlign: "center",
                              }}
                            >
                              データがありません
                            </div>
                          )
                        }
                      </>
                    ) : (
                      <></>
                    )
                  }
                  <div
                    onClick={() => setIsDisplayLabelGroupDetail(!isDisplayLabelGroupDetail)}
                    role="none"
                    style={{
                      margin: "24px 0",
                      textAlign: "center",
                      backgroundColor: "#4572ba",
                      color: "#FFFFFF",
                      padding: "8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}
                  >
                    {isDisplayLabelGroupDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                  </div>
                  <div className="category_title_area">
                    <div
                      className="category_title"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      業務別仕分け実績情報
                      <div className="tools" style={{ justifyContent: "space-between" }}>
                        {
                          login.permissions?.task_cost_view && (
                            <>
                              {
                                isDisplayLabelWorkTime ? (
                                  <div />
                                ) : (
                                  <div className="cost_viewable_label">
                                    コスト表示権限を持つユーザーのみ表示されています
                                  </div>
                                )
                              }
                              <button
                                type="button"
                                className={`display_switch ${isDisplayLabelWorkTime ? "check" : ""}`}
                                onClick={() => setIsDisplayLabelWorkTime((cur) => !cur)}
                              >
                                <div>
                                  <FontAwesomeIcon icon={['far', 'clock']} />
                                  <span>
                                    実績時間
                                  </span>
                                </div>
                                <div>
                                  <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                  <span>
                                    実績コスト
                                  </span>
                                </div>
                              </button>
                            </>
                          )
                        }
                      </div>
                    </div>
                  </div>
                  <ReportSummaryModule
                    list={labelList}
                    type="time"
                    graphTitle="業務別仕分け実績情報"
                    headerTitle={`業務(実績${isDisplayLabelWorkTime ? "時間" : "コスト"}が多い順)`}
                    chartSize={300}
                    valueType={isDisplayLabelWorkTime ? "time" : "cost"}
                  />
                  {
                    isDisplayLabelDetail ? (
                      <>
                        {
                          labelList.length ? (
                            <>
                              <div className="list_row_wrapper">
                                <div className={`list_row header_area${login.permissions?.task_cost_view ? " cost_view" : ""}`}>
                                  <div className="title_display_area">
                                    業務名
                                  </div>
                                  <div className="work_time">
                                    業務時間
                                  </div>
                                  {
                                    login.permissions?.task_cost_view && (
                                      <div className="work_cost">
                                        業務コスト
                                        <Tips
                                          description="コスト表示権限を持つユーザーのみ表示されています"
                                          className="fit"
                                          icon={false}
                                        >
                                          <>
                                            <FontAwesomeIcon icon={['far', 'question-circle']} />
                                          </>
                                        </Tips>
                                      </div>
                                    )
                                  }
                                </div>
                              </div>
                              <div>
                                {
                                  labelList.map((label) => (
                                    <DetailRow
                                      key={`detail_row__${label.name}_${label.id}`}
                                      list={{
                                        ...label,
                                        details: label.label_groups,
                                      }}
                                      type="label"
                                      costView={login.permissions?.task_cost_view || false}
                                      isProject
                                    />
                                  ))
                                }
                              </div>
                            </>
                          ) : (
                            <div
                              style={{
                                marginBottom: "24px",
                                fontSize: "14px",
                                textAlign: "center",
                              }}
                            >
                              データがありません
                            </div>
                          )
                        }
                      </>
                    ) : (
                      <></>
                    )
                  }
                  <div
                    onClick={() => setIsDisplayLabelDetail(!isDisplayLabelDetail)}
                    role="none"
                    style={{
                      marginTop: "24px",
                      textAlign: "center",
                      backgroundColor: "#4572ba",
                      color: "#FFFFFF",
                      padding: "8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}
                  >
                    {isDisplayLabelDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div
                      style={{
                        marginBottom: "24px",
                        padding: "0 24px",
                        fontSize: "14px",
                      }}
                    >
                      <p>
                        工数管理機能を使って業務仕分けをおこなうと、より詳細な分析情報が表示できます。
                      </p>
                      <p>
                        工数管理機能の使い方は
                        <a href="https://qasee.zendesk.com/hc/ja/articles/43881715166617" target="_blank" rel="noreferrer">
                          こちら
                        </a>
                        をご覧ください。
                      </p>
                      <p>
                        是非、ご活用ください。
                      </p>
                    </div>
                    <img
                      className="no_use"
                      src="/static/images/task/image_8.png"
                      alt="工数管理を利用すれば、さらにわかる"
                      style={{
                        width: "100%",
                      }}
                    />
                  </div>
                </>
              )
            }
          </>
        ) : (
          <>
            <Loading />
          </>
        )
      }
    </>
  )
}

export default MemberSummaryModule
